import { ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { useToastEffect } from "../hooks";
import { useGetDocumentImageQuery } from "../services/materialApi";
import LoadingSpinnerComponent from "./LoadingSpinnerComponent";
import Modal from "./modal";

// init pdfjs worker from CDN
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function DocumentViewer({
  courseId,
  documentId,
}: {
  courseId: string;
  documentId: string;
}) {
  const {
    data: presignedLinkDTO,
    isLoading,
    isError,
    error,
  } = useGetDocumentImageQuery({ courseId, documentId });
  useToastEffect({ isError, error });

  const [numPages, setNumPages] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation("sourceCarousel");

  useEffect(() => {
    const CONTAINER_PADDING = 10;
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setPageWidth(container.offsetWidth - CONTAINER_PADDING);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const errorDiv = (
    <div className="m-20 flex flex-col items-center rounded-xl bg-neutral p-12">
      <div className="flex items-center gap-4">
        <ExclamationTriangleIcon className="h-8 w-8" />
        <div className="w-60">{t("errorLoadingPage")}</div>
      </div>
    </div>
  );

  return (
    <div className="w-full space-y-4" ref={containerRef}>
      {isLoading ? (
        <LoadingSpinnerComponent viewHeight="min-h-60" />
      ) : presignedLinkDTO?.url ? (
        <Document
          file={presignedLinkDTO?.url}
          loading={<LoadingSpinnerComponent viewHeight="min-h-60" />}
          onLoadSuccess={onDocumentLoadSuccess}
          error={errorDiv}
          onLoadError={(err) => console.error("PDF Load Error:", err)}
        >
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={pageWidth ?? 600}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      ) : (
        errorDiv
      )}
    </div>
  );
}

export default function SourceCarousel({
  courseId,
  citations,
}: {
  courseId: string;
  citations: string[];
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation(["chatPage"]);

  const prevSlide = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const nextSlide = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.min(prev + 1, citations.length - 1));
  };

  const handleImageClick = (e) => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - left;
    if (clickX < width / 2) {
      prevSlide(e);
    } else {
      nextSlide(e);
    }
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsModalOpen(true)}
        type="button"
        className="btn-primary btn btn-sm text-white"
      >
        {t("showSources")} ({citations.length})
      </button>

      {isModalOpen && (
        <Modal
          id="source-carousel-modal"
          isWider={true}
          title=""
          onCancel={() => setIsModalOpen(false)}
          openOnMount={true}
        >
          <div
            onKeyDown={prevSlide}
            onKeyUp={nextSlide}
            onClick={handleImageClick}
            className="relative flex h-full w-full cursor-pointer items-center justify-center"
          >
            {citations.map((docId, index) => (
              <div
                key={docId}
                className={`absolute top-0 left-0 h-full w-full transition-opacity duration-300 ${
                  index === currentIndex
                    ? "relative opacity-100"
                    : "pointer-events-none opacity-0"
                }`}
              >
                <DocumentViewer courseId={courseId} documentId={docId} />
              </div>
            ))}
            {currentIndex > 0 && (
              <button
                type="button"
                onClick={prevSlide}
                className="-translate-y-1/2 absolute top-1/2 left-0 transform rounded-full bg-gray-700 bg-opacity-50 p-2 hover:bg-opacity-75 focus:outline-none"
              >
                <ArrowLeftIcon className="w-5" />
              </button>
            )}
            {currentIndex < citations.length - 1 && (
              <button
                type="button"
                onClick={nextSlide}
                className="-translate-y-1/2 absolute top-1/2 right-0 transform rounded-full bg-gray-700 bg-opacity-50 p-2 hover:bg-opacity-75 focus:outline-none"
              >
                <ArrowRightIcon className="w-5" />
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
